import { notifyAnalyticsIdentityChanged } from '../../analytics/analytics-identity';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AES, enc } from 'crypto-js';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LocalService {

  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  prefixKeep = [
    'api.url',
    'tenant.',
    'theme',
  ];

  // Only these keys feed the analytics identity (GoogleAnalyticsService.readIdentity).
  // Notifying on every write made analytics re-read config getters that write storage,
  // an endless microtask loop that froze the page.
  private readonly identityKeys = ['auth.user', 'tenant.uuid'];

  // AES decryption is the expensive part of a read, and templates read keys like the
  // tenant name, logo and S3 url on every change detection. Cache the plaintext per
  // ciphertext: comparing the raw stored value is cheap and keeps the cache correct
  // after any write (this tab, another tab, or code touching localStorage directly).
  private readonly decryptCache = new Map<string, { cipher: string; plain: string | null }>();

  constructor() { }

  public saveData(key: string, value: any) {
    if (!this.isBrowser) {
      return;
    }

    if (value !== null && value !== undefined) {
      localStorage.setItem(key, this.encrypt(JSON.stringify(value)));
      this.notifyIfIdentityKey(key);
    }
  }

  public getData(key: string) {
    if (!this.isBrowser) {
      return null;
    }

    if (key.startsWith('data_cache')) {
      return null;
    }

    const plain = this.decryptCached(key, localStorage.getItem(key) || '');

    try {
      return JSON.parse(plain);
    } catch (error) {
      return plain;
    }
  }

  public removeData(key: string) {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem(key);

    this.notifyIfIdentityKey(key);
  }

  public clearData() {
    if (!this.isBrowser) {
      return;
    }

    Object.keys(localStorage).forEach((key) => {
      if (!this.prefixKeep.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
        this.notifyIfIdentityKey(key);
      }
    });

    const fqdn = this.getData('tenant.fqdn');

    if (fqdn) {
      this.clearDataByPrefix('tenant');
    }
  }

  public clearDataByPrefix(prefix: string) {
    if (!this.isBrowser) {
      return;
    }

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
        this.notifyIfIdentityKey(key);
      }
    });
  }

  private notifyIfIdentityKey(key: string) {
    if (this.identityKeys.includes(key)) {
      notifyAnalyticsIdentityChanged();
    }
  }

  private decryptCached(key: string, cipher: string): string | null {
    const cached = this.decryptCache.get(key);

    if (cached && cached.cipher === cipher) {
      return cached.plain;
    }

    const plain = this.decrypt(cipher);
    this.decryptCache.set(key, { cipher, plain });
    return plain;
  }

  private encrypt(text: string) {
    return AES.encrypt(text, environment.localStorageSecretKey).toString();
  }

  private decrypt(text: string) {
    return AES.decrypt(text, environment.localStorageSecretKey).toString(enc.Utf8) || null;
  }

  private generateUrl(url: string, secure: boolean = true) {
    return `${secure ? 'https' : 'http'}://${url}`;
  }

  public initEnvironment() {
    if (!this.isBrowser) {
      return;
    }

    this.saveData('api.url', this.generateUrl(environment.api.url, environment.api.secure));
    this.saveData('s3.url', this.generateUrl(environment.s3.url, environment.s3.secure));
  }
}
